// First-build ghosts (IMPLEMENTATION §0.2 R10, GDD D7 / §8 "Ghosts").
//   Scope: a building is a ghost when its type has requirements and no building of the type was ever
//   completed (`progress.firstBuilt`); a second copy bought while the first is still a ghost is a ghost too
//   (else "buy 2, cancel the ghost" would skip the materials). The first completion builds the type, and
//   every other ghost of it becomes a finished building at once: its delivered items come back like a
//   cancel's (nearest free cells, else credited) and it owes no change — only the first build needs
//   materials (R10). Cash is paid up front. Ghosts are skipped by every system and never fed automatically.
//   Delivery (by hand only): an item fills the first requirement of its family whose tier it meets ("that
//   tier or bigger"); a bigger item fills as many of that requirement's slots as its units cover. The change
//   is the value difference V(item) − filled × V(required tier), owed until the build completes (paying it
//   at delivery would let delivery + cancel mint cash, since cancel returns the whole item).
//   Completion: the delivered items are consumed (ledger consumedBuild, never refunded on a later sale), the
//   change is paid as manual income, the type joins firstBuilt.
//   Cancel (Ghost card or the Sell tool): refunds the cash paid and returns the delivered items to the nearest
//   free cells (BFS from the ghost's cell), or credits 100% of their value when no cell is free.
import { removeBuilding } from './buildings.ts';
import { creditItems, dropHeld } from './containers.ts';
import { cellIndex } from './core/grid.ts';
import type { Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import type { GhostReq, SimDefs } from './defs.ts';
import { itemValue, restoreItem } from './entities.ts';
import type { BType, FamId } from './gen/ids.ts';
import { book } from './ledger.ts';
import { nearestFree } from './occupancy.ts';
import { clearStall } from './stall.ts';
import type { Building, Ghost, ItemRef, SimState } from './state.ts';
import { purgeUndo } from './undoStack.ts';

export function ghostReqs(d: SimDefs, type: BType): readonly GhostReq[] {
  return type === 'deposit' ? [] : d.buildings[type].ghost;
}

/** A newly bought building of `type` is a ghost (first build of a type that has requirements). */
export function isGhostBuild(s: SimState, d: SimDefs, type: BType): boolean {
  return ghostReqs(d, type).length > 0 && !s.progress.firstBuilt.includes(type);
}

export function newGhost(d: SimDefs, type: BType): Ghost {
  return { delivered: [], filled: ghostReqs(d, type).map(() => 0), change: 0 };
}

export interface GhostFill {
  /** Requirement index. */
  req: Int;
  /** Slots it fills. */
  k: Int;
  /** Change owed for it. */
  change: Int;
}

/** What delivering `fam` T`tier` to ghost `b` would fill, or null when the ghost doesn't need it. */
export function ghostFill(d: SimDefs, b: Building, fam: FamId, tier: Int): GhostFill | null {
  if (b.ghost === null) return null;
  const reqs = ghostReqs(d, b.type);
  for (let i = 0; i < reqs.length; i++) {
    const r = reqs[i] as GhostReq;
    const left = r.n - (b.ghost.filled[i] ?? 0);
    if (r.fam !== fam || tier < r.tier || left <= 0) continue;
    const ratio = (d.tierUnits[tier - 1] ?? 1) / (d.tierUnits[r.tier - 1] ?? 1);
    const k = ratio < left ? ratio : left;
    return { req: i, k, change: itemValue(d, fam, tier) - k * itemValue(d, fam, r.tier) };
  }
  return null;
}

/** True when every requirement slot is filled. */
export function ghostDone(d: SimDefs, b: Building): boolean {
  if (b.ghost === null) return false;
  const reqs = ghostReqs(d, b.type);
  for (let i = 0; i < reqs.length; i++) {
    if ((b.ghost.filled[i] ?? 0) < (reqs[i] as GhostReq).n) return false;
  }
  return true;
}

/**
 * Returns a ghost's delivered items to the nearest free cells around (x, y) (BFS; the ghost's own cell
 * first when it is free), crediting 100% of whatever finds no cell (`where: 'ghost'`).
 */
function returnDeliveries(c: Ctx, g: Ghost, x: Int, y: Int, from: Int): void {
  const claimed: boolean[] = [];
  const credit: ItemRef[] = [];
  for (const r of g.delivered) {
    const at = nearestFree(c.occ, x, y, claimed);
    if (at === null) {
      credit.push(r);
      continue;
    }
    claimed[cellIndex(c.occ.cols, at.x, at.y)] = true;
    restoreItem(c, { id: r.id, fam: r.fam, tier: r.tier, x: at.x, y: at.y });
    c.ev.push({ e: 'spawn', id: r.id, fam: r.fam, tier: r.tier, x: at.x, y: at.y, from });
  }
  creditItems(c, credit, 'ghost', x, y);
  g.delivered = [];
}

/** A pending ghost of an already built type becomes a finished building; its deliveries come back. */
function releaseGhost(c: Ctx, b: Building): void {
  const g = b.ghost as Ghost;
  b.ghost = null;
  returnDeliveries(c, g, b.x, b.y, b.id);
  clearStall(c, b);
  c.ev.push({ e: 'built', bid: b.id });
}

function completeGhost(c: Ctx, b: Building): void {
  const g = b.ghost as Ghost;
  for (const r of g.delivered) book(c.s, c.d, r.fam, r.tier, 'consumedBuild', 1);
  b.ghost = null;
  if (g.change > 0) {
    c.s.wallet.cash += g.change;
    c.s.wallet.lifetime += g.change;
    c.s.income.accT += g.change;
    c.ev.push({ e: 'change', bid: b.id, value: g.change });
  }
  if (!c.s.progress.firstBuilt.includes(b.type)) c.s.progress.firstBuilt.push(b.type);
  clearStall(c, b);
  c.ev.push({ e: 'built', bid: b.id });
  // only the first build of a type needs materials: the other pending ghosts of it are built now
  for (const o of c.s.buildings) {
    if (o.id !== b.id && o.type === b.type && o.ghost !== null) releaseGhost(c, o);
  }
}

/** Hand delivery of `r` (already taken from its source) to ghost `b`; completes the build when full. */
export function deliverToGhost(c: Ctx, b: Building, r: ItemRef, fill: GhostFill): void {
  const g = b.ghost as Ghost;
  g.filled[fill.req] = (g.filled[fill.req] ?? 0) + fill.k;
  g.change += fill.change;
  g.delivered.push({ id: r.id, fam: r.fam, tier: r.tier });
  dropHeld(c, r.id);
  c.ev.push({ e: 'deliver', bid: b.id, id: r.id });
  if (ghostDone(c.d, b)) completeGhost(c, b);
}

/** Cancels ghost `b`: refund, items back to free cells (or credited), building removed. */
export function cancelGhost(c: Ctx, b: Building): void {
  const g = b.ghost as Ghost;
  removeBuilding(c, b.id);
  purgeUndo(c, [b.id]);
  c.s.wallet.cash += b.paid;
  returnDeliveries(c, g, b.x, b.y, b.id);
  c.ev.push({ e: 'bremove', bid: b.id, refund: b.paid });
}
